import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useItems, useMarkAsBought, type Item } from '../hooks/useItems';
import { mediaUrl } from '../services/api';

// Demo data — only used when the URL contains ?demo=1, so production behavior
// is unaffected.
const DEMO_ITEMS: Item[] = [
  {
    id: -1,
    name: 'AirPods Pro (2nd gen)',
    description: 'USB-C version. The active noise cancellation is the real selling point.',
    price: 249,
    isBought: false,
    purchaseLink: 'https://www.apple.com/airpods-pro/',
    photo: { url: 'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=600&q=80' },
    category: { name: 'Electronics' },
  },
  {
    id: -2,
    name: 'Keychron Q1 Pro',
    description: 'Wireless mechanical, hot-swappable. Brown switches if there’s a choice.',
    price: 199,
    isBought: false,
    purchaseLink: 'https://www.keychron.com/',
    photo: { url: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=600&q=80' },
    category: { name: 'Electronics' },
  },
  {
    id: -3,
    name: 'Patagonia Better Sweater',
    description: 'Size M, navy. Have wanted this for two winters now.',
    price: 139,
    isBought: true,
    purchaseLink: 'https://www.patagonia.com/',
    photo: { url: 'https://images.unsplash.com/photo-1542060748-10c28b62716f?w=600&q=80' },
    category: { name: 'Clothes' },
  },
  {
    id: -4,
    name: 'LEGO Icons Bonsai Tree',
    description: 'The cherry blossom one. Mostly decorative, mostly satisfying.',
    price: 49,
    isBought: false,
    purchaseLink: 'https://www.lego.com/',
    photo: { url: 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600&q=80' },
    category: { name: 'Legos' },
  },
  {
    id: -5,
    name: 'The Three-Body Problem',
    description: 'Liu Cixin. Hardcover preferred. Already finished book 1 — books 2/3 next.',
    price: 22,
    isBought: false,
    purchaseLink: 'https://bookshop.org/',
    photo: { url: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&q=80' },
    category: { name: 'Books' },
  },
  {
    id: -6,
    name: 'Amazon Gift Card',
    description: 'Any denomination works — perfect for the random small stuff.',
    price: 50,
    isBought: false,
    purchaseLink: 'https://www.amazon.com/gp/product/B004LLIKVU',
    photo: { url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&q=80' },
    category: { name: 'Amazon' },
  },
  {
    id: -7,
    name: 'Lagavulin 16',
    description: 'Peaty, smoky, brilliant. Or any single malt around this profile.',
    price: 95,
    isBought: false,
    purchaseLink: 'https://www.malts.com/',
    photo: { url: 'https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=600&q=80' },
    category: { name: 'Booze' },
  },
  {
    id: -8,
    name: 'Steam Gift Card',
    description: 'Stocking up for the winter sale.',
    price: 25,
    isBought: true,
    purchaseLink: 'https://store.steampowered.com/',
    photo: { url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&q=80' },
    category: { name: 'Gift Cards' },
  },
  {
    id: -9,
    name: 'Cash',
    description: 'Saving for a new desk — every bit gets me closer.',
    price: 0,
    isBought: false,
    purchaseLink: '',
    category: { name: 'Cash' },
  },
];

const isDemoMode = () =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demo');

const CATEGORY_ORDER = [
  'Electronics',
  'Clothes',
  'Legos',
  'Books',
  'Amazon',
  'Booze',
  'Gift Cards',
  'Cash',
];

// Each category gets a small colored signature used in the nav dot,
// the category header accent bar, and the card top stripe.
const CATEGORY_COLORS: Record<string, { dot: string; bar: string; chip: string }> = {
  Electronics: { dot: 'bg-sky-500', bar: 'from-sky-400 to-sky-600', chip: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200' },
  Clothes: { dot: 'bg-pink-500', bar: 'from-pink-400 to-pink-600', chip: 'bg-pink-100 text-pink-800 dark:bg-pink-950/60 dark:text-pink-200' },
  Legos: { dot: 'bg-amber-500', bar: 'from-amber-400 to-amber-600', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200' },
  Books: { dot: 'bg-emerald-500', bar: 'from-emerald-400 to-emerald-600', chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200' },
  Amazon: { dot: 'bg-orange-500', bar: 'from-orange-400 to-orange-600', chip: 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-200' },
  Booze: { dot: 'bg-rose-500', bar: 'from-rose-400 to-rose-600', chip: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200' },
  'Gift Cards': { dot: 'bg-violet-500', bar: 'from-violet-400 to-violet-600', chip: 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200' },
  Cash: { dot: 'bg-green-500', bar: 'from-green-400 to-green-600', chip: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-200' },
};

const defaultCategoryColor = {
  dot: 'bg-slate-400',
  bar: 'from-slate-400 to-slate-600',
  chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};

const colorsFor = (name: string) => CATEGORY_COLORS[name] ?? defaultCategoryColor;

// Cash and gift cards aren't one-time gifts — they can always be contributed
// to, so we never offer a "claim" button or treat them as bought.
const UNCLAIMABLE_CATEGORIES = new Set(['Cash', 'Gift Cards']);
const isUnclaimable = (item: Item) =>
  UNCLAIMABLE_CATEGORIES.has(item.category?.name ?? '');

interface PasswordModalProps {
  isOpen: boolean;
  itemName: string;
  onConfirm: (password: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  error: string;
}

const PasswordModal: React.FC<PasswordModalProps> = ({
  isOpen,
  itemName,
  onConfirm,
  onCancel,
  isLoading,
  error,
}) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(password);
    setPassword('');
  };

  const handleCancel = () => {
    setPassword('');
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-canvas-dark/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-up">
      <div className="bg-canvas-light-elev dark:bg-canvas-dark-elev rounded-3xl shadow-2xl max-w-md w-full p-8 border border-slate-200/70 dark:border-white/5 relative overflow-hidden">
        <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-accent-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <h3 className="text-3xl font-display font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">
            One quick check
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
            Enter the password to mark{' '}
            <span className="font-semibold text-slate-900 dark:text-white">"{itemName}"</span> as
            purchased.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-white/5 dark:text-white rounded-xl focus:ring-2 focus:ring-accent-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            {error && (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-800 dark:text-rose-200 px-4 py-3 rounded-xl text-sm font-medium">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1 bg-transparent hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-50 text-slate-700 dark:text-slate-300 font-semibold py-3 px-4 rounded-xl transition-all duration-200 border border-slate-200 dark:border-white/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !password}
                className="flex-1 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-accent-700 dark:hover:bg-accent-100 disabled:opacity-50 font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying
                  </span>
                ) : (
                  'Confirm purchase'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="rounded-2xl overflow-hidden bg-canvas-light-elev dark:bg-canvas-dark-elev border border-slate-200/70 dark:border-white/5 shadow-card">
    <div className="h-52 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 animate-pulse" />
    <div className="p-5 space-y-3">
      <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
      <div className="h-3 w-full bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
      <div className="h-9 w-full bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse mt-4" />
    </div>
  </div>
);

export const PublicWishlistPage: React.FC = () => {
  const { isDark, toggleDark } = useTheme();
  const [passwordModal, setPasswordModal] = useState({
    isOpen: false,
    itemId: 0,
    itemName: '',
  });
  const [passwordError, setPasswordError] = useState('');

  const { data: realItems = [], isLoading: realLoading } = useItems();
  const markAsBoughtMutation = useMarkAsBought();

  const demo = isDemoMode();
  const items: Item[] = demo ? DEMO_ITEMS : realItems;
  const isLoading = demo ? false : realLoading;

  const categories = Array.from(
    new Set(items.map((item: Item) => item.category?.name).filter(Boolean))
  ) as string[];

  const sortedCategories = categories.sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  const handleMarkAsBoughtClick = (item: Item) => {
    setPasswordModal({ isOpen: true, itemId: item.id, itemName: item.name });
    setPasswordError('');
  };

  const handlePasswordSubmit = async (password: string) => {
    try {
      await markAsBoughtMutation.mutateAsync({
        itemId: passwordModal.itemId,
        password,
      });
      setPasswordModal({ isOpen: false, itemId: 0, itemName: '' });
      setPasswordError('');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Failed to update item';
      setPasswordError(errorMessage);
      console.error('Error updating item:', err);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordModal({ isOpen: false, itemId: 0, itemName: '' });
    setPasswordError('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas-light dark:bg-canvas-dark">
        <div className="max-w-6xl mx-auto px-4 pt-20">
          <div className="h-14 w-72 bg-slate-200 dark:bg-slate-800 rounded-lg animate-pulse mb-4" />
          <div className="h-5 w-48 bg-slate-200 dark:bg-slate-800 rounded animate-pulse mb-16" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas-light dark:bg-canvas-dark text-slate-900 dark:text-slate-100 transition-colors duration-200 relative overflow-hidden">
      {/* Ambient hero glow */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[600px] pointer-events-none bg-hero-glow dark:bg-hero-glow-dark"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        {/* Top bar */}
        <header className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-warm-500 shadow-md" />
            <span className="font-display font-semibold text-lg tracking-tight">wtbc</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="px-3.5 py-2 rounded-full text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-canvas-light-elev dark:hover:bg-white/5 border border-transparent hover:border-slate-200/70 dark:hover:border-white/10 transition-all"
            >
              Admin
            </Link>
            <button
              onClick={toggleDark}
              className="p-2.5 rounded-full bg-canvas-light-elev dark:bg-white/5 border border-slate-200/70 dark:border-white/10 shadow-sm hover:shadow-md hover:bg-white dark:hover:bg-white/10 transition-all"
              title="Toggle theme"
            >
            {isDark ? (
              <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.536l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.121-10.607a1 1 0 010 1.414l-.707.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zm5.657-9.193a1 1 0 00-1.414 0l-.707.707A1 1 0 005.05 6.464l.707-.707a1 1 0 001.414-1.414l-.707-.707zM5 11a1 1 0 100-2H4a1 1 0 100 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
            </button>
          </div>
        </header>

        {/* Hero */}
        <section className="mb-20 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100/70 dark:bg-accent-900/30 border border-accent-200/60 dark:border-accent-500/20 text-accent-700 dark:text-accent-300 text-xs font-medium mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
            Live wishlist · {new Date().getFullYear()}
          </div>
          <h1 className="font-display font-semibold text-slate-900 dark:text-white tracking-tight leading-[0.95] text-6xl sm:text-7xl lg:text-[88px] mb-6">
            What To Buy{' '}
            <span className="italic inline-block pr-2 bg-gradient-to-r from-accent-600 via-accent-500 to-warm-500 bg-clip-text text-transparent">
              Chucks
            </span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed">
            A small, curated collection of things I'd genuinely love. Pick one, mark it claimed,
            and we'll keep everyone in sync so no one doubles up.
          </p>
        </section>

        {/* Sticky category nav */}
        {sortedCategories.length > 0 && (
          <nav className="sticky top-4 z-40 mb-12">
            <div className="bg-canvas-light-elev/80 dark:bg-canvas-dark-elev/80 backdrop-blur-xl border border-slate-200/70 dark:border-white/10 rounded-2xl p-2 shadow-card">
              <div className="flex flex-wrap gap-1 justify-center sm:justify-start">
                {sortedCategories.map((category) => {
                  const c = colorsFor(category);
                  return (
                    <a
                      key={category}
                      href={`#category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById(`category-${category.toLowerCase().replace(/\s+/g, '-')}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="group flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white transition-all whitespace-nowrap"
                    >
                      <span className={`w-2 h-2 rounded-full ${c.dot} ring-2 ring-transparent group-hover:ring-current/10 transition-all`} />
                      {category}
                    </a>
                  );
                })}
              </div>
            </div>
          </nav>
        )}

        {/* Empty state */}
        {sortedCategories.length === 0 ? (
          <div className="text-center py-32">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-accent-100 to-warm-100 dark:from-accent-900/40 dark:to-warm-700/20 mb-6">
              <svg className="w-9 h-9 text-accent-600 dark:text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4m0 0L4 7m16 0v10l-8 4m0-4L4 7m0 10l8 4" />
              </svg>
            </div>
            <p className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-2">
              Nothing here yet
            </p>
            <p className="text-slate-500 dark:text-slate-400">Check back soon — the list is being curated.</p>
          </div>
        ) : (
          <div className="space-y-20">
            {sortedCategories.map((category) => {
              const c = colorsFor(category);
              const categoryItems = (items as Item[]).filter((item: Item) => item.category?.name === category);
              const categoryId = `category-${category.toLowerCase().replace(/\s+/g, '-')}`;

              return (
                <section key={category} id={categoryId} className="scroll-mt-28">
                  {/* Category header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`w-1.5 h-12 rounded-full bg-gradient-to-b ${c.bar}`} />
                    <h2 className="font-display text-4xl font-semibold text-slate-900 dark:text-white tracking-tight">
                      {category}
                    </h2>
                  </div>

                  {/* Items grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryItems.map((item) => (
                      <article
                        key={item.id}
                        className={`group relative rounded-2xl overflow-hidden transition-all duration-300 bg-canvas-light-elev dark:bg-canvas-dark-elev border border-slate-200/70 dark:border-white/5 shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-accent-300/60 dark:hover:border-accent-500/30 ${
                          item.isBought ? 'opacity-70' : ''
                        }`}
                      >
                        {/* Gradient ring on hover */}
                        <div
                          aria-hidden
                          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(245,147,50,0.06))',
                          }}
                        />

                        {/* Category accent stripe */}
                        <div className={`h-1 w-full bg-gradient-to-r ${c.bar}`} />

                        {/* Image */}
                        <div className={`relative h-56 overflow-hidden flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 ${item.isBought ? 'grayscale' : ''}`}>
                          {item.photo?.url ? (
                            <img
                              src={mediaUrl(item.photo.url)}
                              alt={item.name}
                              className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <svg
                              className="w-14 h-14 text-slate-300 dark:text-slate-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}

                          {/* Floating price chip */}
                          {item.price && !item.isBought && (
                            <span className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur text-slate-900 dark:text-white font-display font-semibold text-sm shadow-sm border border-slate-200/70 dark:border-white/10">
                              ${item.price}
                            </span>
                          )}

                          {/* Purchased stamp */}
                          {item.isBought && (
                            <div className="absolute inset-0 flex items-center justify-center bg-canvas-dark/30">
                              <span className="px-5 py-2 rounded-lg border-2 border-emerald-500/80 text-emerald-600 dark:text-emerald-300 font-display font-semibold tracking-widest uppercase text-sm -rotate-6 bg-white/80 dark:bg-canvas-dark/80 backdrop-blur shadow-md">
                                Claimed
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Body */}
                        <div className="p-5 relative">
                          <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-white mb-1.5 leading-snug line-clamp-1">
                            {item.name}
                          </h3>
                          {item.description && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                              {item.description}
                            </p>
                          )}

                          {(item.purchaseLink || (!item.isBought && !isUnclaimable(item))) && (
                          <div className="flex gap-2 mt-4">
                            {!item.isBought && !isUnclaimable(item) && (
                              <button
                                onClick={() => handleMarkAsBoughtClick(item)}
                                className="flex-1 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-accent-700 dark:hover:bg-accent-100 font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 text-sm shadow-sm hover:shadow-md"
                              >
                                I'll get it
                              </button>
                            )}
                            {item.purchaseLink && (
                              <a
                                href={item.purchaseLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-center gap-1.5 ${
                                  item.isBought || isUnclaimable(item) ? 'flex-1' : ''
                                } bg-transparent hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-slate-300 font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 text-sm border border-slate-200 dark:border-white/10`}
                              >
                                View
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                          </div>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-24 pt-8 border-t border-slate-200/70 dark:border-white/5 text-center text-sm text-slate-500 dark:text-slate-400">
          Made with care · {new Date().getFullYear()}
        </footer>
      </div>

      <PasswordModal
        isOpen={passwordModal.isOpen}
        itemName={passwordModal.itemName}
        onConfirm={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        isLoading={markAsBoughtMutation.isPending}
        error={passwordError}
      />
    </div>
  );
};
